import type { CmmdThreadAttachment, CmmdThreadAttachmentDownload } from "@cmmd-forge/contracts";

import { request, type TransportConfig } from "./transport";

/**
 * Cloud persistence for composer file attachments (forge#3516).
 *
 * Lives in its own module rather than growing `index.ts` (already far past its
 * size cap), following the `threadDisposition.ts` pattern: a bound API mixed
 * into the client, riding the same owner-JWT transport as every other
 * forge-context call.
 */
export interface ThreadAttachmentsApi {
  listThreadAttachments(threadId: string): Promise<ReadonlyArray<CmmdThreadAttachment>>;
  uploadThreadAttachment(
    threadId: string,
    file: File,
    attachmentId?: string,
    signal?: AbortSignal,
  ): Promise<CmmdThreadAttachment>;
  getThreadAttachmentDownloadUrl(
    threadId: string,
    attachmentId: string,
  ): Promise<CmmdThreadAttachmentDownload>;
  deleteThreadAttachment(threadId: string, attachmentId: string): Promise<void>;
}

function threadAttachmentPath(threadId: string, suffix = ""): string {
  return `/api/cmmd/thread-attachments/${encodeURIComponent(threadId)}${suffix}`;
}

export function bindThreadAttachmentsApi(cfg: TransportConfig): ThreadAttachmentsApi {
  return {
    async listThreadAttachments(threadId) {
      // Registry route (unwrapData) -> server strips the CMMD envelope -> array
      return request<ReadonlyArray<CmmdThreadAttachment>>(
        { method: "GET", path: threadAttachmentPath(threadId) },
        cfg,
      );
    },
    async uploadThreadAttachment(threadId, file, attachmentId, signal) {
      const form = new FormData();
      form.append("file", file);
      if (attachmentId !== undefined) {
        form.append("attachmentId", attachmentId);
      }
      return request<CmmdThreadAttachment>(
        {
          method: "POST",
          path: threadAttachmentPath(threadId),
          form,
          ...(signal !== undefined ? { signal } : {}),
        },
        cfg,
      );
    },
    async getThreadAttachmentDownloadUrl(threadId, attachmentId) {
      return request<CmmdThreadAttachmentDownload>(
        {
          method: "GET",
          path: threadAttachmentPath(threadId, `/${encodeURIComponent(attachmentId)}/download`),
        },
        cfg,
      );
    },
    async deleteThreadAttachment(threadId, attachmentId) {
      await request<void>(
        {
          method: "DELETE",
          path: threadAttachmentPath(threadId, `/${encodeURIComponent(attachmentId)}`),
        },
        cfg,
      );
    },
  };
}
